/*
 * The pop-up's entry list: ordering, and the merge of the two sources that feed
 * it (relay packets and the /instant_pending reconcile).
 *
 * Its own module, like gen_config.ts and claim.ts, because neither property it
 * protects is observable from the rendered pop-up — it shows no claim state and
 * no arrival time — so the only way to hold them is a unit test.
 *
 * mergeEntries' claim-preservation branch is defensive only: /instant_pending
 * omits claimed rows, so `list` never holds one in the running component.
 */

// goserver's "instant_task" relay payload, which /instant_pending items share.
export interface InstantEntry {
  type?: string;
  task_uuid: string;
  task_type?: string;
  task_label?: string;
  student_uuid?: string;
  student_name?: string;
  assigned_to_ops_uuid?: string;
  assigned_name?: string;
  reason?: string;
  created_at_iso?: string;
  instant_expires_at_iso?: string;
  claimed_by_ops_uuid?: string;
  claimed_by_name?: string;
  arrived_at_ms?: number;
}

// Oldest first: the task closest to expiry is the one a rep should pick up, and
// the wireframe orders the multi-pending list the same way.
export const byAge = (a: InstantEntry, b: InstantEntry): number =>
  String(a.created_at_iso || '').localeCompare(String(b.created_at_iso || ''));

/*
 * Fold a relay packet into the current entries.
 *
 * A relay packet carries no claim state — claimed_by_* is reconcile-only, since
 * the relay fires before anyone can have claimed — so a duplicate packet for a
 * task the reconcile already reported as claimed must not overwrite what we
 * know. (Duplicates are possible: the Redis dedupe claim is released on a relay
 * failure, and several creators call notifyInstantTask.)
 *
 * `arrived_at_ms` is what lets the reconcile tell a packet that landed while its
 * request was in flight from a stale entry it is authoritative over. It is
 * client-only — no endpoint sends or reads it.
 */
export const mergeEntries = (list: InstantEntry[], incoming: InstantEntry): InstantEntry[] => {
  const existing = list.find((e) => e.task_uuid === incoming.task_uuid);
  const merged: InstantEntry = { ...incoming, arrived_at_ms: Date.now() };
  if (existing && !incoming.claimed_by_ops_uuid && existing.claimed_by_ops_uuid) {
    merged.claimed_by_ops_uuid = existing.claimed_by_ops_uuid;
    merged.claimed_by_name = existing.claimed_by_name;
  }
  const next = list.filter((e) => e.task_uuid !== incoming.task_uuid);
  next.push(merged);
  return next.sort(byAge);
};
